import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X } from "lucide-react";
import { VIDEOS } from "../data/content";
import { SectionLabel, Reveal } from "./Reveal";

export default function Visuals() {
  const [active, setActive] = useState(null);

  return (
    <section id="visuals" className="relative w-full py-24 md:py-36 px-5 md:px-10 bg-[color:var(--graphite)]" data-testid="section-visuals">
      <div className="max-w-[1400px] mx-auto">
        <SectionLabel index="02 / VISUALS" id="visuals-label">FILM ARCHIVE</SectionLabel>
        <h2 className="font-display chrome-text text-5xl sm:text-6xl md:text-8xl leading-[0.9] mb-14 md:mb-20">
          <Reveal text="VISUALS" />
        </h2>

        <div className="grid gap-5 md:gap-6 md:grid-cols-6">
          {VIDEOS.map((v, i) => (
            <motion.button
              key={v.id}
              onClick={() => setActive(v)}
              data-testid={`video-card-${v.id}`}
              data-cursor
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-8% 0px" }}
              transition={{ duration: 0.8, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className={`group relative overflow-hidden rounded-sm ring-1 ring-white/10 text-left ${
                i === 0 ? "md:col-span-4 md:row-span-2" : "md:col-span-2"
              }`}
            >
              <img
                src={v.thumb}
                alt={v.title}
                className={`w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105 ${
                  i === 0 ? "h-64 md:h-full" : "h-56 md:h-64"
                }`}
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 rounded-full glass-strong flex items-center justify-center scale-90 opacity-70 group-hover:opacity-100 group-hover:scale-100 transition-all duration-500">
                  <Play size={20} className="text-white fill-white ml-1" />
                </div>
              </div>
              <div className="absolute bottom-0 left-0 p-5 md:p-7">
                <p className="track-label text-white/50 mb-2">{v.type} · {v.year}</p>
                <h3 className="font-display steel-text text-2xl md:text-4xl leading-none">{v.title}</h3>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            className="fixed inset-0 z-[9600] flex items-center justify-center p-4 md:p-10 bg-black/90 backdrop-blur-2xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            data-testid="video-modal"
          >
            <motion.div
              className="relative w-full max-w-5xl aspect-video bg-black ring-1 ring-white/15"
              initial={{ scale: 0.92, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.92, y: 30 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              {active.file ? (
                <video src={active.file} controls autoPlay className="w-full h-full" />
              ) : (
                <iframe
                  title={active.title}
                  src={active.embed + "?autoplay=1"}
                  className="w-full h-full"
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                />
              )}
            </motion.div>
            <button
              onClick={() => setActive(null)}
              data-testid="video-modal-close"
              className="absolute top-5 right-5 w-11 h-11 rounded-full glass flex items-center justify-center text-white/80 hover:text-white"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
